import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader, type PageCreateAction } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { batchesInfiniteQueryOptions } from '../api/publish.api';
import { PUBLISH_PANEL_PATH } from '../constants/publish.constants';
import { useBatchFilters, type BatchPanelStage } from '../hooks/useBatchFilters';
import { BatchFilterPanel } from './BatchFilterPanel';
import { getBatchColumns, type BatchRowAction } from './BatchColumns';

interface BatchPanelPageProps {
  stage: BatchPanelStage;
  title: string;
  emptyMessage: string;
  rowAction?: BatchRowAction | undefined;
  createAction?: PageCreateAction | undefined;
  children?: ReactNode;
}

export function BatchPanelPage({
  stage,
  title,
  emptyMessage,
  rowAction,
  createAction,
  children,
}: BatchPanelPageProps) {
  const controller = useBatchFilters(stage);
  const { search } = controller;
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    batchesInfiniteQueryOptions(stage, search),
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedBatchId = useRevealLastViewedRow('publishBatches');
  const columns = useMemo(
    () =>
      getBatchColumns(
        (batch) => (
          <Link
            to="/publish/batches/$batchId"
            params={{ batchId: batch.batchId }}
            search={{ from: PUBLISH_PANEL_PATH[stage] }}
          />
        ),
        rowAction,
      ),
    [stage, rowAction],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={<DateRangeBadge dateFrom={search.dateFrom} dateTo={search.dateTo} />}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={createAction}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <BatchFilterPanel filters={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the batches"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="batch"
          emptyMessage={emptyMessage}
          isRowHighlighted={(batch) => batch.batchId === lastViewedBatchId}
          {...loadMoreProps}
        />
      )}

      {children}
    </div>
  );
}
